
import { phases, allMonths } from '../../../mocks/roadmapData';

export default function MilestoneOverview() {
  const allMilestones = phases.flatMap((phase) =>
    phase.milestones.map((ms) => ({
      ...ms,
      phaseTitle: phase.title,
      phasePhase: phase.phase,
      color: phase.color,
    }))
  );

  allMilestones.sort((a, b) => a.month - b.month);

  const colorMap: Record<string, { dot: string; bg: string; text: string; ring: string }> = {
    rose: { dot: 'bg-rose-500', bg: 'bg-rose-50', text: 'text-rose-700', ring: 'ring-rose-200' },
    amber: { dot: 'bg-amber-500', bg: 'bg-amber-50', text: 'text-amber-700', ring: 'ring-amber-200' },
    teal: { dot: 'bg-teal-600', bg: 'bg-teal-50', text: 'text-teal-700', ring: 'ring-teal-200' },
    emerald: { dot: 'bg-emerald-600', bg: 'bg-emerald-50', text: 'text-emerald-700', ring: 'ring-emerald-200' },
    cyan: { dot: 'bg-cyan-600', bg: 'bg-cyan-50', text: 'text-cyan-700', ring: 'ring-cyan-200' },
  };

  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-14">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">핵심 마일스톤 로드맵</h2>
          <p className="text-base text-gray-500 max-w-2xl mx-auto">
            프로젝트의 주요 이정표를 시간순으로 정리했습니다. 완료된 항목은 체크 표시로 확인할 수 있습니다.
          </p>
        </div>

        {/* Horizontal Timeline Bar */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 mb-14 overflow-x-auto">
          <div className="min-w-[900px] relative">
            <div className="absolute top-5 left-0 right-0 h-1 bg-gray-200 rounded-full"></div>
            <div className="flex justify-between relative">
              {allMilestones.map((ms, index) => {
                const leftPercent = (ms.month / (allMonths.length - 1)) * 100;
                const c = colorMap[ms.color];
                return (
                  <div
                    key={index}
                    className="absolute flex flex-col items-center"
                    style={{ left: `${leftPercent}%`, transform: 'translateX(-50%)' }}
                  >
                    <div className={`w-4 h-4 rounded-full ${ms.done ? 'bg-teal-500' : c.dot} border-4 border-white shadow-md z-10 ${ms.done ? 'ring-2 ring-teal-200' : `ring-2 ${c.ring}`}`}></div>
                    {ms.done && (
                      <div className="absolute -top-1 -right-1 w-3 h-3 bg-white rounded-full flex items-center justify-center z-20">
                        <i className="ri-check-fill text-teal-600" style={{ fontSize: '8px' }}></i>
                      </div>
                    )}
                    <div className={`mt-4 px-3 py-2 ${ms.done ? 'bg-teal-50 border-teal-200' : `${c.bg} ${c.ring.replace('ring-', 'border-')}`} border rounded-lg shadow-sm`}>
                      <div className={`text-xs font-bold ${ms.done ? 'text-teal-700' : c.text} whitespace-nowrap`}>{ms.name}</div>
                      <div className="text-xs text-gray-400 text-center mt-0.5">{allMonths[ms.month]}</div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="h-28"></div>
          </div>
        </div>

        {/* Milestone Cards Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {allMilestones.map((ms, index) => {
            const c = colorMap[ms.color];
            return (
              <div
                key={index}
                className={`bg-white rounded-xl p-5 border ${ms.done ? 'border-teal-200' : 'border-gray-100'} hover:shadow-lg transition-all cursor-default group`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-3 h-3 rounded-full ${ms.done ? 'bg-teal-500' : c.dot}`}></div>
                    <span className={`text-xs font-bold ${c.text} uppercase`}>{ms.phasePhase}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <i className="ri-calendar-line text-xs text-gray-400"></i>
                    <span className="text-xs text-gray-400 font-medium">{allMonths[ms.month]}</span>
                  </div>
                </div>
                <h4 className="text-base font-bold text-gray-900 mb-1.5 flex items-center gap-2">
                  {ms.name}
                  {ms.done && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-teal-100 text-teal-700 text-xs font-semibold rounded-full">
                      <i className="ri-check-line text-xs"></i>완료
                    </span>
                  )}
                </h4>
                <p className="text-sm text-gray-500">{ms.phaseTitle}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
